import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Loader2, Lock, ArrowRight } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChatWidget = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showQuickPicks, setShowQuickPicks] = useState(true);
  const [isGated, setIsGated] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const QUICK_PICKS = [
    { label: 'The Taxera Difference', message: 'What makes Taxera different from other tax compliance platforms?' },
    { label: 'What does it cost?', message: 'Can you tell me about Taxera pricing and how it works?' },
    { label: 'Show me a quick demo', message: "I'd like to see a demo of the Taxera platform. How can I arrange that?" },
    { label: 'I have a compliance question', message: 'I have a specific tax compliance question I need help with.' },
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => { scrollToBottom(); }, [messages, suggestions]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const greeting = user
        ? `Welcome back, ${user.name?.split(' ')[0] || 'there'}! I'm Nolan — how can I help you with tax compliance today?`
        : t('chat.greeting');
      setMessages([{ role: 'assistant', content: greeting }]);
    }
    if (isOpen) inputRef.current?.focus();
  }, [isOpen, t, messages.length, user]);

  // If user logs in while gated, unlock
  useEffect(() => {
    if (user && isGated) setIsGated(false);
  }, [user, isGated]);

  const sendMessage = useCallback(async (overrideMessage) => {
    const userMessage = (overrideMessage || input).trim();
    if (!userMessage || isLoading || isGated) return;

    setInput('');
    setShowQuickPicks(false);
    setSuggestions([]);
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chat/message`, {
        session_id: sessionId,
        message: userMessage,
        user_id: user?.user_id || null,
      }, { withCredentials: true });

      const { response: botText, gated, suggestions: newSuggestions } = response.data;

      setMessages(prev => [...prev, { role: 'assistant', content: botText }]);

      if (gated) {
        setIsGated(true);
        setSuggestions([]);
      } else {
        setSuggestions(newSuggestions || []);
      }
    } catch (error) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I apologize, but I'm having trouble connecting right now. Please try again or contact us directly at office@taxera.tech"
      }]);
    } finally {
      setIsLoading(false);
    }
  }, [input, isLoading, isGated, sessionId, user]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Chat Button */}
      <motion.button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-24 right-4 sm:right-6 z-50 w-14 h-14 rounded-full taxera-gradient text-white shadow-lg hover:shadow-xl flex items-center justify-center ${isOpen ? 'hidden' : ''}`}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        data-testid="chat-widget-button"
      >
        <MessageCircle className="w-6 h-6" />
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-20 right-3 left-3 sm:left-auto sm:right-6 z-50 sm:w-[400px] h-[75vh] sm:h-[560px] max-h-[600px] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
            data-testid="chat-widget-window"
          >
            {/* Header */}
            <div className="taxera-gradient px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">{t('chat.title')}</h3>
                  <p className="text-xs text-white/70">
                    {user ? 'Unlimited access' : 'Tax Technology Assistant'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
                data-testid="chat-close-btn"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50">
              {messages.map((message, index) => (
                <motion.div
                  key={message.id ?? `msg-${index}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] px-4 py-3 rounded-2xl ${
                      message.role === 'user'
                        ? 'bg-taxera-blue text-white rounded-br-md'
                        : 'bg-white text-taxera-charcoal border border-slate-200 rounded-bl-md'
                    }`}
                  >
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
                  </div>
                </motion.div>
              ))}

              {/* Quick Pick Buttons (initial) */}
              {showQuickPicks && messages.length <= 1 && !isLoading && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="flex flex-col gap-2 pt-1"
                >
                  {QUICK_PICKS.map((pick, i) => (
                    <motion.button
                      key={`qp-${pick.label.slice(0, 20)}`}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.4 + i * 0.08 }}
                      onClick={() => sendMessage(pick.message)}
                      className="text-left px-4 py-2.5 bg-white border border-taxera-teal/30 rounded-xl text-sm text-taxera-charcoal hover:border-taxera-teal hover:bg-taxera-teal/[0.04] transition-all duration-200"
                      data-testid={`quick-pick-${i}`}
                    >
                      {pick.label}
                    </motion.button>
                  ))}
                </motion.div>
              )}

              {/* Follow-up suggestions */}
              {suggestions.length > 0 && !isLoading && !isGated && (
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="flex flex-wrap gap-2 pt-1"
                >
                  {suggestions.map((s, i) => (
                    <button
                      key={`sg-${s.slice(0, 30)}`}
                      onClick={() => sendMessage(s)}
                      className="px-3 py-1.5 text-xs font-medium bg-white border border-slate-200 rounded-full text-taxera-blue hover:border-taxera-blue hover:bg-taxera-blue/5 transition-all"
                      data-testid={`suggestion-${i}`}
                    >
                      {s}
                    </button>
                  ))}
                </motion.div>
              )}

              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-white border border-slate-200 px-4 py-3 rounded-2xl rounded-bl-md">
                    <Loader2 className="w-5 h-5 text-taxera-teal animate-spin" />
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Gate / Input */}
            {isGated && !user ? (
              <div className="p-4 bg-gradient-to-t from-slate-100 to-white border-t border-slate-200">
                <div className="flex items-center gap-2 mb-3">
                  <Lock className="w-4 h-4 text-taxera-blue" />
                  <span className="text-sm font-semibold text-taxera-charcoal">Free messages used</span>
                </div>
                <p className="text-xs text-slate-500 mb-3">
                  Sign up for unlimited chat, saved history, and personalised recommendations.
                </p>
                <a
                  href="/login"
                  className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl taxera-gradient text-white text-sm font-semibold hover:shadow-lg transition-all"
                  data-testid="chat-signup-cta"
                >
                  Create free account <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            ) : (
              <div className="p-4 bg-white border-t border-slate-200">
                <div className="flex gap-2">
                  <input
                    ref={inputRef}
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={t('chat.placeholder')}
                    className="flex-1 px-4 py-3 bg-slate-100 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-taxera-blue/20"
                    data-testid="chat-input"
                  />
                  <button
                    onClick={() => sendMessage()}
                    disabled={!input.trim() || isLoading}
                    className="w-12 h-12 rounded-xl taxera-gradient text-white flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg transition-all"
                    data-testid="chat-send-btn"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;
